import { getUserId } from "../../../lib/effectiveAuthUser";
import { useConfirmation } from "src/sudokit/components/ConfirmationDialog/ConfirmationProvider";
import React, { memo, useEffect, useCallback, useRef } from "react";
import { useState } from "react";
import { StyledContainer } from "./styles";
import { db } from "../../../Firebase";
import Auth from "../../../Auth";
import { useImmer } from "use-immer";
import Utils, { makeId } from "../../../Utils";
import { debounce } from "lodash";
import Textarea from "react-textarea-autosize";
import classNames from "classnames";
import "firebase/auth";
import { useToast } from "../../ToastWrapper";


function Chat({ projectId }) {
  const confirmAction = useConfirmation(projectId);
  const [message, setMessage] = useState("");
  const [isStreaming, setIsStreaming] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const chatRef = useRef(null);
  const { setToast } = useToast();

  const handleInputChange = useCallback(
    (event) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        sendMessage(message);
        setMessage("");
      } else {
        setMessage(event.target.value);
      }
    },
    [message]
  );

  const [chats, setChats] = useImmer([]);

  const cleanedChats = chats?.map(({ content, role }) => ({ content, role }));

  // load chats
  useEffect(() => {
    db.collection("storyChats")
      .where("projectId", "==", projectId)
      .where("deleted", "==", false)
      .where("userId", "==", getUserId())
      .orderBy("createdAt", "asc")
      .get()
      .then((snapshot) => {
        const newChats = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setChats((draft) => {
          return newChats;
        });
      });
  }, []);

  // scroll to bottom when chats change
  useEffect(() => {
    chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [chats]);

  const saveMessage = async (role, message, { invocationId = null } = {}) => {
    try {
      if (message && message.length > 0) {
        const res = await Auth.fetchWithIdToken(`/api/story-chats/`, {
          role: role,
          content: message,
          projectId,
          invocationId,
        });

        return res;
      }
    } catch (err) {
      setToast("Error saving chat");
      throw err;
    }
  };

  const deleteAllChats = async () => {
    try {
      await Auth.fetchWithIdToken(`/api/story-chats/`, { method: "DELETE", projectId });

      setChats((draft) => []);
    } catch (err) {
      setToast("Error deleting chats");
      throw err;
    }
  };

  const sendMessage = (messageToSend, save = true) => {
    const nonce = makeId(10); // generate a nonce to identify the assistant message

    setChats((draft) => {
      if (save) {
        draft.push({
          role: "user",
          content: messageToSend,
          createdAt: new Date(),
          insighted: false,
        });
      }

      draft.push({
        role: "assistant",
        content: "",
        createdAt: new Date(),
        insighted: false,
        nonce,
      });
    });

    if (save) {
      saveMessage("user", messageToSend);
    }

    let content = "";
    let invocationId = null;

    streamRequest(
      "/api/ai/storyChat/chat",
      {
        history: cleanedChats, //
        message: messageToSend,
        projectId,
      },
      (text) => {
        setChats((draft) => {
          const draftIndex = draft.findIndex((item) => item.nonce === nonce);
          if (draftIndex !== -1) {
            draft[draftIndex].content += text;
            content += text;
          }
        });
      },
      null,
      (chunk) => {
        if (chunk.documentTitle) {
          setChats((draft) => {
            const draftIndex = draft.findIndex((item) => item.nonce === nonce);
            if (draftIndex !== -1) {
              if (!draft[draftIndex].documentTitles) {
                draft[draftIndex].documentTitles = [chunk.documentTitle];
              } else {
                draft[draftIndex].documentTitles.push(chunk.documentTitle);
              }
            }
          });
        }

        if (chunk.invocationId) {
          invocationId = chunk.invocationId;
        }
      },
      () => {
        saveMessage("assistant", content, { invocationId });
      }
    );
  };

  // debounced stream request
  const streamRequest = async (url, data, update, abortSignal = null, onChunk = null, onEnd = null) => {
    setRequesting(true);

    let timeoutId = null;

    let buffer = "";

    // debounce the update function to prevent too many renders
    // and do it more aggressively if there are a lot of scenes
    const debouncedUpdate = debounce(
      () => {
        update(buffer);
        buffer = "";
      },
      25,
      {
        maxWait: 25,
      }
    );

    try {
      const result = await Auth.streamWithIdToken(
        url,
        data,
        abortSignal,
        (chunk) => {
          if (update && chunk.text) {
            if (!isStreaming) setIsStreaming(true);

            // concatenate into a buffer and let debounce let it through
            buffer += chunk.text;
            debouncedUpdate();
          }

          if (onChunk) {
            onChunk(chunk);
          }

          if (chunk.error) {
            setIsStreaming(false);
            if (timeoutId) {
              clearTimeout(timeoutId);
            }

            setToast(chunk.error);
          }
        },
        () => {
          setIsStreaming(false);
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          if (onEnd) {
            onEnd();
          }
        },
        (error) => {
          setToast(error.message);
          setIsStreaming(false);
        }
      );

      return result;
    } catch (e) {
      setRequesting(false);
      throw e;
    }
  };

  return (
    <StyledContainer>
      <div className={classNames("wrapper", { fullscreen })}>
        {fullscreen && <h2>Chat</h2>}
        <div
          className="chats"
          ref={chatRef}
        >
          {chats.map((item, index) => {
            const date = item.createdAt.toDate ? item.createdAt.toDate() : item.createdAt;
            const role = item.role === "user" ? "You" : "Editor";

            return (
              <div
                className="line"
                key={index}
              >
                <div className="role">{role}</div>
                <div className="content">
                  {item.documentTitles && item.documentTitles.length > 0 && (
                    <div>
                      {item.documentTitles.map((title, index) => {
                        return (
                          <div
                            className="reading"
                            key={index}
                          >
                            Referencing '{title}'
                          </div>
                        );
                      })}
                    </div>
                  )}


                  {role !== "user" && !item.content && <div className="think">Thinking...</div>}
                  {item.content && Utils.nl2br(item.content)}
                </div>
                {/* <span>{moment(date).fromNow()}</span> */}
              </div>
            );
          })}
        </div>

        <div className="send">
          <Textarea
            value={message}
            onChange={handleInputChange}
            onKeyDown={handleInputChange}
            placeholder="Chat with me about your story..."
          />
        </div>

        <div className="actions">
          <a
            onClick={async e => {
              e.preventDefault();
              await confirmAction({
                title: "Clear all chats",
                message: "Are you sure you want to clear all chats?",
                actionLabel: "Clear all",
                destructive: true
              }, async () => {
                await deleteAllChats();
              });
            }}
          >
            Clear all
          </a>
        </div>
      </div>
    </StyledContainer>
  );
}

export default memo(Chat);
